<template>
  <div class="landing">
    <!-- ===== Top strip: links institucionais CEPE ===== -->
    <div class="top-strip">
      <div class="container-xl strip-inner">
        <span class="strip-label">Rede CEPE</span>
        <nav class="strip-links">
          <a
            v-for="(link, idx) in institutionalLinks"
            :key="idx"
            :href="link.link"
            target="_blank"
            rel="noopener"
          >{{ link.title }}</a>
        </nav>
      </div>
    </div>

    <!-- ===== Header ===== -->
    <header class="site-header">
      <div class="container-xl header-inner">
        <div class="brand">
          <img :src="logoCepe" alt="CEPE" class="brand-logo" />
          <span class="brand-divider"></span>
          <span class="brand-title">Portal do Desenvolvedor</span>
        </div>
        <nav class="header-nav">
          <a href="#recursos">Recursos</a>
          <a href="#como-funciona">Como funciona</a>
          <a href="#exemplo">Exemplo</a>
          <button class="btn-cta btn-cta--sm" @click="goLogin">Entrar</button>
        </nav>
      </div>
    </header>

    <!-- ===== Hero ===== -->
    <section class="hero">
      <div class="container-xl hero-grid">
        <div class="hero-copy">
          <span class="eyebrow">API do Diário Oficial de Pernambuco</span>
          <h1>
            Integre o <span class="hl">Diário Oficial</span><br />
            direto na sua aplicação
          </h1>
          <p class="lead">
            Busca em texto livre, contagem por categoria e estimativa de custo
            sobre todo o acervo de publicações do Diário Oficial do Estado de
            Pernambuco — via uma API REST autenticada por token.
          </p>
          <div class="hero-actions">
            <button class="btn-cta" @click="goLogin">
              Acessar o portal
              <b-icon-arrow-right class="ml-1" />
            </button>
            <a href="#recursos" class="btn-ghost">Ver recursos</a>
          </div>
          <div class="hero-meta">
            <div class="meta-item">
              <strong>+300 mil</strong>
              <span>publicações indexadas</span>
            </div>
            <div class="meta-item">
              <strong>Desde 2019</strong>
              <span>acervo histórico</span>
            </div>
            <div class="meta-item">
              <strong>REST + JWT</strong>
              <span>integração simples</span>
            </div>
          </div>
        </div>

        <!-- Code card -->
        <div class="hero-code">
          <div class="code-window">
            <div class="code-bar">
              <span class="dot dot--red"></span>
              <span class="dot dot--amber"></span>
              <span class="dot dot--green"></span>
              <span class="code-file">busca.sh</span>
            </div>
            <pre class="code-body"><span class="c-cmt"># Consulta o Diário Oficial por texto e período</span>
<span class="c-kw">curl</span> --request POST \
  --url <span class="c-str">{{ baseUrl }}/api/fullTextSearch</span> \
  --header <span class="c-str">'Authorization: Bearer &lt;token&gt;'</span> \
  --header <span class="c-str">'Content-Type: application/json'</span> \
  --data <span class="c-str">'{
    "texto": "nomeação",
    "chave_api": "sua-chave",
    "dataInicio": "2026-01-01",
    "dataFim": "2026-06-30"
  }'</span></pre>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== Recursos ===== -->
    <section id="recursos" class="section">
      <div class="container-xl">
        <div class="section-head">
          <span class="eyebrow">Recursos</span>
          <h2>Tudo que você precisa para consultar o Diário</h2>
          <p>Endpoints objetivos, autenticados e prontos para produção.</p>
        </div>
        <div class="cards">
          <article v-for="(f, i) in features" :key="i" class="card-feat">
            <div class="card-icon">
              <component :is="f.icon" />
            </div>
            <h3>{{ f.title }}</h3>
            <p>{{ f.text }}</p>
            <code class="route">{{ f.route }}</code>
          </article>
        </div>
      </div>
    </section>

    <!-- ===== Como funciona ===== -->
    <section id="como-funciona" class="section section--alt">
      <div class="container-xl">
        <div class="section-head">
          <span class="eyebrow">Como funciona</span>
          <h2>Da chave à resposta em três passos</h2>
        </div>
        <div class="steps">
          <div v-for="(s, i) in steps" :key="i" class="step">
            <div class="step-num">{{ i + 1 }}</div>
            <h3>{{ s.title }}</h3>
            <p>{{ s.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== Exemplo / Base URL ===== -->
    <section id="exemplo" class="section">
      <div class="container-xl exemplo-grid">
        <div class="exemplo-copy">
          <span class="eyebrow">Comece agora</span>
          <h2>Aponte para a URL base de produção</h2>
          <p>
            Todos os endpoints são concatenados à URL base. Autentique-se em
            <code>/user/signin</code> para obter o token JWT e use a
            <code>chave_api</code> do seu projeto nas consultas.
          </p>
          <div class="baseurl">
            <span class="baseurl-tag">BASE URL</span>
            <code>{{ baseUrl }}</code>
            <button class="copy-btn" @click="copyBaseUrl">
              <b-icon-clipboard v-if="!copied" />
              <b-icon-check2 v-else />
            </button>
          </div>
          <button class="btn-cta" @click="goLogin">
            Entrar no portal
            <b-icon-arrow-right class="ml-1" />
          </button>
        </div>
        <div class="exemplo-code">
          <div class="code-window">
            <div class="code-bar">
              <span class="dot dot--red"></span>
              <span class="dot dot--amber"></span>
              <span class="dot dot--green"></span>
              <span class="code-file">custo.sh</span>
            </div>
            <pre class="code-body"><span class="c-cmt"># Estima o custo de uma requisição</span>
<span class="c-kw">curl</span> --request POST \
  --url <span class="c-str">{{ baseUrl }}/api/docsCost</span> \
  --header <span class="c-str">'Authorization: Bearer &lt;token&gt;'</span> \
  --header <span class="c-str">'Content-Type: application/json'</span> \
  --data <span class="c-str">'{
    "texto": "licitação",
    "chave_api": "sua-chave",
    "dataInicio": "2026-06-01",
    "dataFim": "2026-06-11"
  }'</span>

<span class="c-cmt"># → { "hits": 14, "custo_total_requisicao": 28 }</span></pre>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CTA band ===== -->
    <section class="cta-band">
      <div class="container-xl cta-inner">
        <div>
          <h2>Pronto para integrar?</h2>
          <p>Acesse o portal para gerenciar projetos, chaves e consultar a documentação completa.</p>
        </div>
        <button class="btn-cta btn-cta--light" @click="goLogin">
          Acessar o portal
          <b-icon-arrow-right class="ml-1" />
        </button>
      </div>
    </section>

    <!-- ===== Footer ===== -->
    <footer class="site-footer">
      <div class="container-xl footer-grid">
        <div class="footer-brand">
          <img :src="logoCepe" alt="CEPE" class="footer-logo" />
          <p>
            Companhia Editora de Pernambuco — Portal do Desenvolvedor da API
            do Diário Oficial do Estado.
          </p>
        </div>
        <div class="footer-links">
          <span class="footer-title">Rede CEPE</span>
          <a
            v-for="(link, idx) in institutionalLinks"
            :key="idx"
            :href="link.link"
            target="_blank"
            rel="noopener"
          >{{ link.title }}</a>
        </div>
        <div class="footer-links">
          <span class="footer-title">Portal</span>
          <a href="#recursos">Recursos</a>
          <a href="#como-funciona">Como funciona</a>
          <a href="#exemplo">Exemplo</a>
          <a href="#" @click.prevent="goLogin">Entrar</a>
        </div>
      </div>
      <div class="footer-bottom container-xl">
        <span>© {{ year }} CEPE — Companhia Editora de Pernambuco</span>
        <span>Ainda não tem acesso? Fale com o administrador da sua empresa.</span>
      </div>
    </footer>
  </div>
</template>

<script>
import logoCepe from '@/assets/img/logo-cepe-light.png';
import headerConfig from '@/assets/json/headerConfig.json';

export default {
  name: 'landing-page',
  data() {
    return {
      logoCepe,
      baseUrl: 'https://api.portal-open-api.sdoe.com.br',
      copied: false,
      year: new Date().getFullYear(),
      institutionalLinks: headerConfig.links,
      features: [
        {
          icon: 'b-icon-shield-lock',
          title: 'Token de Acesso (JWT)',
          text: 'Autentique-se e receba um token JWT para assinar todas as requisições à API.',
          route: 'POST /user/signin'
        },
        {
          icon: 'b-icon-search',
          title: 'Busca em Texto Livre',
          text: 'Pesquise por termos e período em todo o acervo, filtrado pelas categorias do seu projeto.',
          route: 'POST /api/fullTextSearch'
        },
        {
          icon: 'b-icon-calculator',
          title: 'Custo de Requisição',
          text: 'Estime quantos documentos uma busca retorna e o custo correspondente antes de consumir.',
          route: 'POST /api/docsCost'
        },
        {
          icon: 'b-icon-bar-chart',
          title: 'Contagem por Categoria',
          text: 'Conte documentos por categoria e texto, com o custo estimado da operação.',
          route: 'POST /api/docsCount'
        },
        {
          icon: 'b-icon-list-ul',
          title: 'Listar Categorias',
          text: 'Descubra quais categorias estão autorizadas para a sua chave de API.',
          route: 'POST /api/categories'
        }
      ],
      steps: [
        {
          title: 'Autentique-se',
          text: 'Faça login em /user/signin com seu e-mail e senha e receba o token JWT de acesso.'
        },
        {
          title: 'Use sua chave_api',
          text: 'Cada projeto possui uma chave de API que define as categorias e os custos da consulta.'
        },
        {
          title: 'Consulte o Diário',
          text: 'Envie texto e período para buscar, contar ou estimar o custo sobre as publicações.'
        }
      ]
    };
  },
  created() {
    window.document.title = 'Portal do Desenvolvedor — API do Diário Oficial de PE';
  },
  methods: {
    goLogin() {
      this.$router.push({ name: 'login' }).catch(() => {});
    },
    copyBaseUrl() {
      const done = () => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.baseUrl).then(done).catch(() => {});
      } else {
        done();
      }
    }
  }
};
</script>

<style scoped>
.landing {
  --red: #861f1f;
  --red-hover: #972929;
  --ink: #16181d;
  --muted: #5b6470;
  --line: #e7e9ee;
  --bg-alt: #f6f4f2;
  font-family: 'Kelson Sans', Avenir, Helvetica, Arial, sans-serif;
  color: var(--ink);
  background: #fff;
  text-align: left;
}

.container-xl {
  width: 100%;
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ---------- Top strip ---------- */
.top-strip {
  background: var(--red);
  color: #fff;
  font-size: 12px;
}
.strip-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;
  gap: 16px;
}
.strip-label {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
  opacity: 0.85;
  white-space: nowrap;
}
.strip-links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  justify-content: flex-end;
}
.strip-links a {
  color: #fff;
  font-size: 12px;
  text-decoration: none;
  opacity: 0.92;
  transition: opacity 0.2s;
}
.strip-links a:hover { opacity: 0.6; }

/* ---------- Header ---------- */
.site-header {
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(17, 18, 22, 0.96);
  backdrop-filter: blur(6px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
}
.brand { display: flex; align-items: center; gap: 14px; }
.brand-logo { height: 38px; width: auto; }
.brand-divider { width: 1px; height: 26px; background: rgba(255, 255, 255, 0.22); }
.brand-title {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.header-nav { display: flex; align-items: center; gap: 28px; }
.header-nav a {
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
  font-size: 14.5px;
  transition: color 0.2s;
}
.header-nav a:hover { color: #fff; }

/* ---------- Buttons ---------- */
.btn-cta {
  display: inline-flex;
  align-items: center;
  background: var(--red);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}
.btn-cta:hover { background: var(--red-hover); }
.btn-cta:active { transform: translateY(1px); }
.btn-cta--sm { padding: 8px 16px; font-size: 14px; }
.btn-cta--light { background: #fff; color: var(--red); }
.btn-cta--light:hover { background: #f1eeec; }
.btn-ghost {
  display: inline-flex;
  align-items: center;
  padding: 12px 22px;
  border-radius: 8px;
  border: 1px solid var(--line);
  color: var(--ink);
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
  transition: border-color 0.2s, background 0.2s;
}
.btn-ghost:hover { border-color: #c9ccd3; background: #fafafa; }

/* ---------- Hero ---------- */
.hero {
  position: relative;
  background:
    radial-gradient(900px 360px at 85% -8%, rgba(134, 31, 31, 0.16), transparent 60%),
    linear-gradient(180deg, #fbfafa 0%, #fff 100%);
  padding: 84px 0 72px;
  border-bottom: 1px solid var(--line);
}
.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 56px;
  align-items: center;
}
/* Permite que as colunas encolham abaixo do min-content (evita overflow no mobile) */
.hero-grid > *,
.exemplo-grid > * { min-width: 0; }
.eyebrow {
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--red);
  margin-bottom: 14px;
}
.hero-copy h1 {
  font-size: 46px;
  line-height: 1.08;
  font-weight: 700;
  margin: 0 0 18px;
  letter-spacing: -0.01em;
}
.hero-copy h1 .hl { color: var(--red); }
.lead {
  font-size: 18px;
  line-height: 1.6;
  color: var(--muted);
  max-width: 540px;
  margin: 0 0 30px;
}
.hero-actions { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 40px; }
.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 36px;
  padding-top: 26px;
  border-top: 1px solid var(--line);
}
.meta-item { display: flex; flex-direction: column; }
.meta-item strong { font-size: 22px; color: var(--ink); }
.meta-item span { font-size: 13.5px; color: var(--muted); }

/* ---------- Code window ---------- */
.code-window {
  background: #14161c;
  border-radius: 12px;
  overflow: hidden;
  max-width: 100%;
  box-shadow: 0 24px 60px rgba(17, 18, 22, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.code-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #1c1f27;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }
.dot--red { background: #ff5f56; }
.dot--amber { background: #ffbd2e; }
.dot--green { background: #27c93f; }
.code-file {
  margin-left: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12.5px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}
.code-body {
  margin: 0;
  padding: 20px;
  color: #d7dbe3;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
}
.c-cmt { color: #6b7280; }
.c-kw { color: #ff7b72; }
.c-str { color: #7ee787; }

/* ---------- Sections ---------- */
.section { padding: 80px 0; }
.section--alt { background: var(--bg-alt); }
.section-head { text-align: center; max-width: 660px; margin: 0 auto 48px; }
.section-head h2 {
  font-size: 32px;
  font-weight: 700;
  margin: 8px 0 12px;
  letter-spacing: -0.01em;
}
.section-head p { color: var(--muted); font-size: 17px; margin: 0; }

/* ---------- Feature cards ---------- */
.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
.card-feat {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 28px 24px;
  transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
}
.card-feat:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 34px rgba(17, 18, 22, 0.08);
  border-color: #dfe2e8;
}
.card-icon {
  width: 46px;
  height: 46px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(134, 31, 31, 0.08);
  color: var(--red);
  font-size: 22px;
  margin-bottom: 18px;
}
.card-feat h3 { font-size: 18px; font-weight: 700; margin: 0 0 8px; }
.card-feat p { color: var(--muted); font-size: 14.5px; line-height: 1.55; margin: 0 0 16px; }
.route {
  display: inline-block;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: var(--red);
  background: rgba(134, 31, 31, 0.07);
  padding: 5px 10px;
  border-radius: 6px;
}

/* ---------- Steps ---------- */
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.step {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 30px 26px;
}
.step-num {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--red);
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.step h3 { font-size: 18px; font-weight: 700; margin: 0 0 8px; }
.step p { color: var(--muted); font-size: 14.5px; line-height: 1.55; margin: 0; }

/* ---------- Exemplo ---------- */
.exemplo-grid {
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  gap: 52px;
  align-items: center;
}
.exemplo-copy h2 { font-size: 30px; font-weight: 700; margin: 8px 0 14px; }
.exemplo-copy p { color: var(--muted); font-size: 16.5px; line-height: 1.6; margin: 0 0 24px; }
.exemplo-copy code {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.9em;
  color: var(--red);
  background: rgba(134, 31, 31, 0.07);
  padding: 2px 6px;
  border-radius: 5px;
}
.baseurl {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #14161c;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 26px;
}
.baseurl-tag {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #fff;
  background: var(--red);
  padding: 4px 8px;
  border-radius: 5px;
}
.baseurl code {
  flex: 1;
  min-width: 0;
  color: #d7dbe3;
  background: none;
  padding: 0;
  font-size: 14px;
  overflow-x: auto;
  white-space: nowrap;
}
.copy-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 34px;
  height: 34px;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.copy-btn:hover { background: rgba(255, 255, 255, 0.2); }

/* ---------- CTA band ---------- */
.cta-band {
  background:
    radial-gradient(600px 240px at 80% 0%, rgba(255, 255, 255, 0.08), transparent),
    var(--red);
  color: #fff;
}
.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 56px 24px;
  flex-wrap: wrap;
}
.cta-inner h2 { font-size: 28px; font-weight: 700; margin: 0 0 6px; }
.cta-inner p { margin: 0; opacity: 0.9; font-size: 16px; }

/* ---------- Footer ---------- */
.site-footer { background: #14161c; color: rgba(255, 255, 255, 0.7); }
.footer-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 36px;
  padding: 56px 24px 36px;
}
.footer-logo { height: 40px; margin-bottom: 16px; }
.footer-brand p { font-size: 14px; line-height: 1.6; max-width: 320px; margin: 0; }
.footer-links { display: flex; flex-direction: column; gap: 10px; }
.footer-title {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 4px;
}
.footer-links a {
  color: rgba(255, 255, 255, 0.66);
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}
.footer-links a:hover { color: #fff; }
.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 22px;
  padding-bottom: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
  flex-wrap: wrap;
}

/* ---------- Responsive ---------- */
@media (max-width: 900px) {
  .hero-grid,
  .exemplo-grid { grid-template-columns: 1fr; gap: 36px; }
  .cards,
  .steps { grid-template-columns: 1fr 1fr; }
  .hero-copy h1 { font-size: 38px; }
  .header-nav a { display: none; }
  .footer-grid { grid-template-columns: 1fr; gap: 28px; }
}
@media (max-width: 560px) {
  .cards,
  .steps { grid-template-columns: 1fr; }
  .strip-links { display: none; }
  .hero { padding: 56px 0 48px; }
  .hero-copy h1 { font-size: 31px; }
  .hero-meta { gap: 22px; }
  .cta-inner { flex-direction: column; align-items: flex-start; }
}
</style>
